import { useState, useRef, ChangeEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import imageCompression from 'browser-image-compression'
import {
  Image as ImageIcon,
  Send,
  Loader2,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Card, CardContent } from '@/components/ui/card'
import { postsApi, mediaApi, User, UploadedMedia } from '@/lib/api'
import { getInitials } from '@/lib/utils'
import { useToast } from '@/hooks/useToast'
import { EmojiPicker } from '@/components/EmojiPicker'

interface PostComposerProps {
  user?: User | null
}

interface PreviewImage {
  file: File
  preview: string
  uploaded?: UploadedMedia
}

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_FILES = 4
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

export function PostComposer({ user }: PostComposerProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const [content, setContent] = useState('')
  const [images, setImages] = useState<PreviewImage[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [cursorPosition, setCursorPosition] = useState<number>(0)

  const createPostMutation = useMutation({
    mutationFn: async () => {
      // If we have images, upload them first
      let mediaIds: string[] = []

      if (images.length > 0 && !images.every(img => img.uploaded)) {
        setIsUploading(true)
        try {
          const filesToUpload = images.filter(img => !img.uploaded).map(img => img.file)
          const response = await mediaApi.uploadPhotos(filesToUpload)

          // Handle single or multiple upload response
          const uploadedMedia = Array.isArray(response.data) ? response.data : [response.data]

          // Update images with uploaded data
          setImages(prevImages => {
            const newImages = [...prevImages]
            let uploadIndex = 0
            for (let i = 0; i < newImages.length; i++) {
              if (!newImages[i].uploaded) {
                newImages[i].uploaded = uploadedMedia[uploadIndex]
                uploadIndex++
              }
            }
            return newImages
          })

          mediaIds = uploadedMedia.map(m => m.id)
        } catch (error) {
          console.error('Upload error:', error)
          throw new Error('Failed to upload images')
        } finally {
          setIsUploading(false)
        }
      } else if (images.length > 0) {
        // All images already uploaded
        mediaIds = images.map(img => img.uploaded!.id)
      }

      // Determine post type
      const postType = mediaIds.length > 1 ? 'album' : mediaIds.length === 1 ? 'photo' : 'text'

      // Create post
      return postsApi.createPost({
        content: content.trim() || undefined,
        post_type: postType,
        visibility: 'public',
        media_ids: mediaIds.length > 0 ? mediaIds : undefined,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] })
      setContent('')
      setImages([])
      toast({
        title: 'Post created',
        description: 'Your post has been shared successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create post',
        description: error.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      })
    },
  })

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])

    // Validation
    if (images.length + files.length > MAX_FILES) {
      toast({
        title: 'Too many files',
        description: `You can upload up to ${MAX_FILES} images per post.`,
        variant: 'destructive',
      })
      return
    }

    const validFiles: File[] = []
    const errors: string[] = []

    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.`)
        continue
      }

      // Compress image if it's too large (skip GIFs to preserve animation)
      let processedFile = file
      if (file.size > MAX_FILE_SIZE && file.type !== 'image/gif') {
        try {
          const options = {
            maxSizeMB: 9.5, // Slightly under 10MB to leave room for compression overhead
            maxWidthOrHeight: 2048,
            useWebWorker: true,
            fileType: file.type as 'image/jpeg' | 'image/png' | 'image/webp',
          }
          processedFile = await imageCompression(file, options)

          // If still too large after compression, reject
          if (processedFile.size > MAX_FILE_SIZE) {
            errors.push(`${file.name}: File is too large even after compression. Try a smaller image.`)
            continue
          }
        } catch (err) {
          console.error('Compression error:', err)
          errors.push(`${file.name}: Failed to compress image.`)
          continue
        }
      } else if (file.size > MAX_FILE_SIZE) {
        // GIF is too large and we can't compress it
        errors.push(`${file.name}: File size exceeds 10MB.`)
        continue
      }

      validFiles.push(processedFile)
    }

    if (errors.length > 0) {
      toast({
        title: 'Invalid files',
        description: errors.join('\n'),
        variant: 'destructive',
      })
    }

    if (validFiles.length > 0) {
      const newImages = validFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }))
      setImages((prev) => [...prev, ...newImages])
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const removeImage = (index: number) => {
    setImages((prev) => {
      const newImages = [...prev]
      URL.revokeObjectURL(newImages[index].preview)
      newImages.splice(index, 1)
      return newImages
    })
  }

  const handleSubmit = () => {
    if (!content.trim() && images.length === 0) {
      toast({
        title: 'Empty post',
        description: 'Please add some content or photos to your post.',
        variant: 'destructive',
      })
      return
    }
    createPostMutation.mutate()
  }

  const handleEmojiSelect = (emoji: string) => {
    if (!textareaRef.current) return

    // Get the current cursor position
    const start = textareaRef.current.selectionStart
    const end = textareaRef.current.selectionEnd

    // Insert emoji at cursor position
    const newContent = content.slice(0, start) + emoji + content.slice(end)
    setContent(newContent)

    // Set cursor position after the emoji
    const newCursorPosition = start + emoji.length
    setCursorPosition(newCursorPosition)

    // Focus back on textarea and set cursor position
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
      }
    }, 0)
  }

  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value)
    setCursorPosition(e.target.selectionStart)
  }

  const handleTextareaClick = () => {
    if (textareaRef.current) {
      setCursorPosition(textareaRef.current.selectionStart)
    }
  }

  const handleTextareaKeyUp = () => {
    if (textareaRef.current) {
      setCursorPosition(textareaRef.current.selectionStart)
    }
  }

  const isPosting = createPostMutation.isPending || isUploading

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex gap-4">
          <Avatar>
            <AvatarImage src={user?.avatarUrl} />
            <AvatarFallback>{getInitials(user?.displayName || 'U')}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={handleTextChange}
              onClick={handleTextareaClick}
              onKeyUp={handleTextareaKeyUp}
              placeholder="What's on your mind?"
              className="w-full resize-none border-0 bg-transparent text-sm focus:outline-none focus:ring-0"
              rows={3}
              disabled={isPosting}
            />

            {/* Image previews */}
            <AnimatePresence>
              {images.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 overflow-hidden rounded-lg"
                >
                  <div className={`grid gap-2 ${images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    {images.map((image, index) => (
                      <motion.div
                        key={image.preview}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="relative aspect-square overflow-hidden rounded-lg bg-muted"
                      >
                        <img
                          src={image.preview}
                          alt={`Preview ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                        <button
                          onClick={() => removeImage(index)}
                          disabled={isPosting}
                          className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white transition-colors hover:bg-black/80 disabled:opacity-50"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        {image.uploaded && (
                          <div className="absolute bottom-2 left-2 rounded bg-green-500/90 px-2 py-0.5 text-xs text-white">
                            Uploaded
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-3 flex items-center justify-between border-t pt-3">
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isPosting || images.length >= MAX_FILES}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isPosting || images.length >= MAX_FILES}
                  type="button"
                >
                  <ImageIcon className="mr-1 h-4 w-4" />
                  Photo {images.length > 0 && `(${images.length}/${MAX_FILES})`}
                </Button>
                <EmojiPicker onEmojiSelect={handleEmojiSelect} disabled={isPosting} />
              </div>
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={isPosting || (!content.trim() && images.length === 0)}
              >
                {isPosting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                {isUploading ? 'Uploading...' : 'Post'}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
